import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../helpers/AuthContext';
import Section from '../Section';
import HelpArticleLink from './HelpArticleLink';
import './SellerOnboardingTodos.css';

const PLAN_TYPES = {
    basic: new Set(['monthly', 'year']),
    standard: new Set(['cc-monthly', 'cc-year', 'free-forever']),
};
const HIDDEN_TODOS_STORAGE_KEY = 'seller-onboarding-hidden-todos';

function getHiddenTodoIds() {
    try {
        const storedIds = JSON.parse(localStorage.getItem(HIDDEN_TODOS_STORAGE_KEY) || '[]');
        return new Set(Array.isArray(storedIds) ? storedIds : []);
    } catch {
        return new Set();
    }
}

function getPlanLevel(plan = {}) {
    if (plan.tier === 'standard' || PLAN_TYPES.standard.has(plan.type)) {
        return 'standard';
    }
    if (plan.tier === 'basic' || PLAN_TYPES.basic.has(plan.type)) {
        return 'basic';
    }
    return 'trial';
}

function formatCount(count, singular, plural = `${singular}s`) {
    return `${count} ${count === 1 ? singular : plural}`;
}

function createManualPaymentsTodo(payments) {
    return {
        id: 'payments',
        title: 'Set up payment methods',
        description: payments.hasManualPaymentMethod
            ? 'A manual PayPal, CashApp, or Venmo payment method is ready.'
            : 'Add PayPal, CashApp, or Venmo so customers can pay you.',
        href: '/account/payments/links',
        helpSlug: 'basic-payments',
        done: Boolean(payments.hasManualPaymentMethod),
        cta: payments.hasManualPaymentMethod ? 'Manage payment links' : 'Set up payments',
    };
}

/**
 * Red: the payout profile is the get-paid gate, never the sell gate. A 2257-approved seller sells
 * on CCBill right away, and those sales are held until the payout profile is approved, so the
 * checklist must not send them to PayPal, CashApp, or Venmo payment links.
 */
function createRedPayoutTodo(redSales) {
    let description;
    let done = Boolean(redSales.payoutReady);
    if (!redSales.canSell) {
        description = 'Finish your 2257 compliance documents to start selling. '
            + 'Your payout profile is what lets you get paid.';
        done = false;
    } else if (redSales.payoutReady) {
        description = 'Your payout profile is approved. '
            + 'Request a payout to get paid for your sales.';
    } else if (redSales.sellingBeforePayout) {
        description = 'You can sell now. Your CCBill sales are held until your payout profile is approved.';
    } else {
        description = 'You can sell now; finish your payout profile to get paid.';
    }

    return {
        id: 'payments',
        title: 'Set up payment methods',
        description,
        href: '/account/payments/profile',
        helpSlug: 'credit-card-payments',
        done,
        cta: done ? 'Manage payments' : 'Complete payout setup',
    };
}

/**
 * @typedef {Object} SellerOnboardingTodo
 * @property {string} id
 * @property {string} title
 * @property {string} description
 * @property {string} href
 * @property {string} helpSlug
 * @property {boolean} done
 * @property {string} cta
 */

/**
 * Convert seller onboarding status into the plan-specific checklist shown in the UI.
 *
 * @param {Object} status
 * @returns {{planLevel: string, planName: string, todos: SellerOnboardingTodo[]}}
 */
export function buildSellerOnboardingTodos(status = {}) {
    const channel = status.channel || {};
    const plan = status.plan || {};
    const storefront = status.storefront || {};
    const payments = status.payments || {};
    const compliance = status.compliance || {};
    const verification = status.verification || {};
    const redSales = status.redSales || null;
    const planLevel = getPlanLevel(plan);
    const planIsSelected = planLevel !== 'trial' && plan.isValid !== false;
    const planName = planLevel === 'trial' ? 'Free Trial' : (plan.name || planLevel);
    const socialLinkCount = Number(storefront.socialLinkCount) || 0;
    const linkCount = Number(storefront.linkCount) || 0;
    const productCount = Number(storefront.productCount) || 0;
    const blogPostCount = Number(storefront.blogPostCount) || 0;

    const todos = [
        {
            id: 'plan',
            title: planIsSelected ? `${planName} is active` : 'Choose your seller plan',
            description: planIsSelected
                ? `Your ${planLevel} features are ready to use.`
                : (channel.isRed
                    ? 'Choose a Standard plan to sell on Mayhem Red.'
                    : 'Choose Basic for manual payments or Standard for integrated payments.'),
            href: '/account/plan',
            helpSlug: 'compare-plans',
            done: planIsSelected,
            cta: planIsSelected ? 'Review plan' : 'Compare plans',
        },
        {
            id: 'social-links',
            title: 'Set up your social links',
            description: socialLinkCount > 0
                ? `${formatCount(socialLinkCount, 'social link')} added to your storefront.`
                : 'Help customers find and follow you on your other platforms.',
            href: '/account/landingpage/socials',
            helpSlug: 'store-customization',
            done: socialLinkCount > 0,
            cta: socialLinkCount > 0 ? 'Manage social links' : 'Add social links',
        },
        {
            id: 'storefront-link',
            title: 'Add your first storefront link',
            description: linkCount > 0
                ? `${formatCount(linkCount, 'link')} added to your storefront.`
                : 'Make your storefront useful as a link-in-bio page.',
            href: '/account/landingpage/links',
            helpSlug: 'store-customization',
            done: linkCount > 0,
            cta: linkCount > 0 ? 'Manage links' : 'Add a link',
        },
    ];

    if (channel.isRed) {
        let complianceDescription = 'Submit your required identity and release documents.';
        const needsComplianceResubmission = compliance.status === 'rejected';
        if (compliance.status === 'submitted') {
            complianceDescription = 'Your 2257 compliance documents are waiting for approval.';
        } else if (compliance.status === 'approved') {
            // PLAN-RED-CCBILL-DEFAULT-CHECKOUT Phase 5: 2257 is the listing/sales gate on Red;
            // the payout profile is only the payout gate.
            complianceDescription = 'Your approved 2257 lets you list and sell on Mayhem Red.';
        } else if (compliance.status === 'rejected') {
            complianceDescription = 'Your documents were not approved. Review and resubmit them.';
        }

        todos.push({
            id: '2257-compliance',
            title: needsComplianceResubmission
                ? 'Re-submit 2257 compliance documents'
                : compliance.hasSubmitted2257
                ? '2257 compliance submitted'
                : 'Submit 2257 compliance documents',
            description: complianceDescription,
            href: '/account/compliance',
            helpSlug: '2257-compliance',
            done: Boolean(compliance.hasSubmitted2257) && !needsComplianceResubmission,
            cta: needsComplianceResubmission
                ? 'Re-submit documents'
                : compliance.hasSubmitted2257 ? 'Review compliance' : 'Submit documents',
        });
    }

    const needsStandaloneVerification = planLevel !== 'standard'
        || plan.type === 'free-forever';
    if (needsStandaloneVerification && !verification.isVerified) {
        const isPending = verification.submissionStatus === 'submitted';
        todos.push({
            id: 'verified-seller',
            title: 'Get verified',
            description: isPending
                ? 'Your verification is waiting for review.'
                : 'Verify your identity to earn the Verified Seller badge.',
            href: '/account/verification',
            helpSlug: 'verified-seller',
            done: false,
            cta: isPending ? 'View status' : 'Get verified',
        });
    }

    if (planLevel !== 'standard') {
        todos.push(channel.isRed && redSales?.sellingBeforePayout
            ? createRedPayoutTodo(redSales)
            : createManualPaymentsTodo(payments));
    }

    if (planLevel === 'standard') {
        if (verification.isEligibleForPayoutProfile === false) {
            todos.push(createManualPaymentsTodo(payments));
        } else if (channel.isRed && redSales) {
            todos.push(createRedPayoutTodo(redSales));
        } else {
            let paymentDescription = 'Complete your payout profile to accept integrated payments.';
            if (payments.payoutProfileStatus === 'submitted') {
                paymentDescription = 'Your payout profile was submitted and is waiting for approval.';
            } else if (payments.hasIntegratedPayments) {
                paymentDescription = 'Integrated payments are ready for your customers.';
            } else if (payments.payoutProfileStatus) {
                paymentDescription = `Your payout profile is ${payments.payoutProfileStatus}.`;
            }

            todos.push({
                id: 'payments',
                title: 'Set up payment methods',
                description: paymentDescription,
                href: '/account/payments/profile',
                helpSlug: 'credit-card-payments',
                done: Boolean(payments.hasIntegratedPayments),
                cta: payments.hasIntegratedPayments ? 'Manage payments' : 'Complete payout setup',
            });
        }
    }

    todos.push({
        id: 'first-product',
        title: 'Create your first product',
        description: productCount > 0
            ? `${formatCount(productCount, 'product')} in your catalog.`
            : 'Create a product listing so customers have something to buy.',
        href: '/account/products#create',
        helpSlug: 'product-settings',
        done: productCount > 0,
        cta: productCount > 0 ? 'Manage products' : 'Create a product',
    });

    todos.push({
        id: 'first-blog-post',
        title: 'Publish your first blog post',
        description: blogPostCount > 0
            ? `${formatCount(blogPostCount, 'post')} published.`
            : 'Introduce your work and give customers a reason to return.',
        href: '/account/blog',
        helpSlug: 'content-seo-guide',
        done: blogPostCount > 0,
        cta: blogPostCount > 0 ? 'Manage blog' : 'Write a post',
    });

    return { planLevel, planName, todos };
}

function OnboardingTodo({ todo, onHide }) {
    return (
        <li className={`seller-onboarding-todo ${todo.done ? 'is-done' : 'is-open'}`}>
            <span
                className="seller-onboarding-todo__status"
                role="img"
                aria-label={todo.done ? 'Complete' : 'Not complete'}
            >
                <i className={todo.done ? 'fa-solid fa-circle-check' : 'fa-regular fa-circle'}></i>
            </span>
            <div className="seller-onboarding-todo__body">
                <div className="seller-onboarding-todo__title-row">
                    <strong>{todo.title}</strong>
                    <HelpArticleLink
                        slug={todo.helpSlug}
                        label={`Help with ${todo.title}`}
                        variant="icon"
                    />
                </div>
                <p>{todo.description}</p>
            </div>
            <div className="seller-onboarding-todo__actions">
                <a className="seller-onboarding-todo__action" href={todo.href}>
                    {todo.cta}
                    <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
                </a>
                <button
                    type="button"
                    className="seller-onboarding-todo__hide"
                    onClick={() => onHide(todo.id)}
                    aria-label={`Hide ${todo.title}`}
                >
                    Hide
                    <i className="fa-regular fa-eye" aria-hidden="true"></i>
                </button>
            </div>
        </li>
    );
}

export default function SellerOnboardingTodos({
    autoHideWhenComplete = true,
    initialStatus = null,
}) {
    const { fetch, API_URL } = useAuth();
    const [status, setStatus] = useState(initialStatus);
    const [loading, setLoading] = useState(!initialStatus);
    const [error, setError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [hiddenTodoIds, setHiddenTodoIds] = useState(getHiddenTodoIds);

    useEffect(() => {
        if (initialStatus) return undefined;

        let isActive = true;
        const loadStatus = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await fetch(`${API_URL}/v1/seller/onboarding`);
                if (!response.ok) {
                    throw new Error('Could not load onboarding progress.');
                }
                const result = await response.json();
                if (isActive) setStatus(result.data);
            } catch (loadError) {
                if (isActive) setError(loadError.message);
            } finally {
                if (isActive) setLoading(false);
            }
        };

        loadStatus();
        return () => {
            isActive = false;
        };
    }, [API_URL, fetch, initialStatus, reloadKey]);

    const checklist = useMemo(
        () => status ? buildSellerOnboardingTodos(status) : null,
        [status],
    );
    const completedCount = checklist
        ? checklist.todos.filter(todo => todo.done).length
        : 0;
    const allComplete = Boolean(
        checklist
        && checklist.todos.length > 0
        && completedCount === checklist.todos.length,
    );
    const visibleTodos = checklist
        ? checklist.todos.filter(todo => !hiddenTodoIds.has(todo.id))
        : [];

    if (autoHideWhenComplete && allComplete) return null;
    if (checklist && visibleTodos.length === 0) return null;

    const hideTodo = todoId => {
        setHiddenTodoIds(currentIds => {
            const nextIds = new Set(currentIds);
            nextIds.add(todoId);
            localStorage.setItem(HIDDEN_TODOS_STORAGE_KEY, JSON.stringify([...nextIds]));
            return nextIds;
        });
    };

    let preview = 'Loading your setup progress…';
    if (checklist) {
        preview = `${completedCount} of ${checklist.todos.length} complete · ${checklist.planName}`;
    } else if (error) {
        preview = 'Setup progress is temporarily unavailable.';
    }

    return (
        <Section
            id="seller-onboarding-todos"
            className="seller-onboarding-todos"
            title="Seller setup checklist"
            preview={preview}
        >
            <div className="seller-onboarding-todos__intro">
                <div>
                    <p className="seller-onboarding-todos__eyebrow">Getting started</p>
                    <p>Finish these essentials, then share your storefront with customers.</p>
                </div>
                <HelpArticleLink
                    slug="getting-started-sellers"
                    label="Read the seller getting started guide"
                    variant="link"
                />
            </div>

            {loading && (
                <p className="seller-onboarding-todos__state" role="status">
                    <i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
                    Loading checklist…
                </p>
            )}

            {!loading && error && (
                <div className="seller-onboarding-todos__state seller-onboarding-todos__error">
                    <p role="alert">{error}</p>
                    <button type="button" onClick={() => setReloadKey(key => key + 1)}>
                        Try again
                    </button>
                </div>
            )}

            {!loading && !error && checklist && (
                <>
                    <div className="seller-onboarding-todos__progress">
                        <div className="seller-onboarding-todos__progress-copy">
                            <strong>{completedCount} of {checklist.todos.length} complete</strong>
                            <span>{checklist.planName}</span>
                        </div>
                        <div
                            className="seller-onboarding-todos__progress-track"
                            role="progressbar"
                            aria-label="Seller setup progress"
                            aria-valuemin="0"
                            aria-valuemax={checklist.todos.length}
                            aria-valuenow={completedCount}
                        >
                            <span
                                style={{
                                    width: `${(completedCount / checklist.todos.length) * 100}%`,
                                }}
                            ></span>
                        </div>
                    </div>

                    {allComplete && (
                        <p className="seller-onboarding-todos__complete">
                            <i className="fa-solid fa-circle-check" aria-hidden="true"></i>
                            Your seller setup is complete.
                        </p>
                    )}

                    <ol className="seller-onboarding-todos__list">
                        {visibleTodos.map(todo => (
                            <OnboardingTodo key={todo.id} todo={todo} onHide={hideTodo} />
                        ))}
                    </ol>
                </>
            )}
        </Section>
    );
}
